import {
  loadPersistedResumableUploadSessions,
  removePersistedResumableUploadSession,
  type PersistedResumableEegUploadSession,
} from "@shared/services/resumableEegUploadSessionStore";
import { probeResumableUploadSession } from "@shared/services/resumableEegUploadClient";

export interface ResumableUploadOffer {
  session: PersistedResumableEegUploadSession;
  uploadedChunks: number;
  totalChunks: number;
  progress: number;
  complete: boolean;
}

export interface ReconcileResumableUploadsResult {
  offers: ResumableUploadOffer[];
  removedUploadIds: string[];
  completedUploadIds: string[];
  /** importTaskIds linked to sessions that finished (/status status=completed) */
  completedImportTaskIds: string[];
}

export async function reconcilePersistedResumableUploads(): Promise<ReconcileResumableUploadsResult> {
  const persisted = loadPersistedResumableUploadSessions();
  if (persisted.length === 0) {
    return {
      offers: [],
      removedUploadIds: [],
      completedUploadIds: [],
      completedImportTaskIds: [],
    };
  }

  const offers: ResumableUploadOffer[] = [];
  const removedUploadIds: string[] = [];
  const completedUploadIds: string[] = [];
  /** uploadId → importTaskId for sessions that completed on the server */
  const completedImportTaskIds: string[] = [];

  await Promise.all(
    persisted.map(async (session) => {
      const probe = await probeResumableUploadSession(session.uploadId);
      if (probe.state === "dead") {
        removePersistedResumableUploadSession(session.uploadId);
        removedUploadIds.push(session.uploadId);
        return;
      }
      if (probe.state === "completed") {
        removePersistedResumableUploadSession(session.uploadId);
        completedUploadIds.push(session.uploadId);
        if (session.importTaskId) {
          completedImportTaskIds.push(session.importTaskId);
        }
        return;
      }
      offers.push({
        session,
        uploadedChunks: probe.uploadedChunks,
        totalChunks: probe.totalChunks,
        progress: probe.progress,
        complete: probe.complete,
      });
    }),
  );

  return {
    offers,
    removedUploadIds,
    completedUploadIds,
    completedImportTaskIds,
  };
}

export function getLiveResumableUploadIds(
  offers: ResumableUploadOffer[],
): Set<string> {
  return new Set(offers.map((offer) => offer.session.uploadId));
}

export function getLiveImportTaskIds(offers: ResumableUploadOffer[]): Set<string> {
  return new Set(
    offers
      .map((offer) => offer.session.importTaskId)
      .filter((taskId): taskId is string => Boolean(taskId)),
  );
}
